import type { Reporting_ChartType, Reporting_ReportFilter } from '@tyro/api';
import { BentoGrid, type BentoGridProps } from '@tyro/core';
import { useState } from 'react';
import type { Widget } from '../../@types/widget';
import type { InnerReportFilter } from '../../api/keys';
import { mapWidgetsToBentoGridLayouts } from '../../utils/map-widgets-to-bento-grid-layout';
import {
  ReportChartWidget,
  type ReportChartWidgetProps,
} from './widgets/chart';
import {
  ReportTableWidget,
  type ReportTableWidgetProps,
} from './widgets/table';

export enum ReportWidgetType {
  Chart = 'chart',
  Table = 'table',
}

export type ReportWidget = Omit<Widget, 'config'> & {
  config:
    | {
        type: ReportWidgetType.Chart;
        chartType: Reporting_ChartType;
        title?: ReportChartWidgetProps['title'];
        options?: ReportChartWidgetProps['options'];
        filter?: Partial<ReportChartWidgetProps['filter']>;
      }
    | {
        type: ReportWidgetType.Table;
        filter?: Partial<ReportTableWidgetProps['filter']>;
      };
};

export interface ReportWidgetsProps extends Partial<BentoGridProps> {
  widgets: ReportWidget[];
  reportFilter?: InnerReportFilter;
}

export function ReportWidgets({
  widgets,
  reportFilter,
  ...bentoGridProps
}: ReportWidgetsProps) {
  const [layouts, setLayouts] = useState<BentoGridProps['layouts']>(
    mapWidgetsToBentoGridLayouts(widgets),
  );

  return (
    <BentoGrid
      {...bentoGridProps}
      onLayoutChange={(_currentLayout, allLayouts) => setLayouts(allLayouts)}
      layouts={layouts}
    >
      {widgets.map(({ id, config }) => {
        const widgetType = config.type;

        if (widgetType === ReportWidgetType.Chart) {
          const { title, chartType, options } = config;
          return (
            <div key={id}>
              <ReportChartWidget
                title={title}
                chartType={chartType}
                filter={
                  {
                    ...(reportFilter?.filter ?? {}),
                    ...config.filter,
                  } as Reporting_ReportFilter
                }
                options={options}
              />
            </div>
          );
        }

        if (widgetType === ReportWidgetType.Table) {
          return (
            <div key={id}>
              <ReportTableWidget
                filter={
                  { ...reportFilter, ...config.filter } as InnerReportFilter
                }
              />
            </div>
          );
        }

        return null;
      })}
    </BentoGrid>
  );
}
